import { proto } from '@hiero-ledger/proto';

import type { PublicLedgerMessage, PublicLedgerTransaction } from '../models/public-ledger.js';
import { ed25519Raw, ledgerKeyFromRule } from './public-ledger-key-rule.js';
import { messagePartText } from './public-ledger-message-parts.js';

// The browser's check of a public ledger transaction before it signs it (#24 phase 2). The server serves a ScheduleSign to
// sign; nothing in the signature says what the schedule does, so the check reads the schedule's contents twice and asserts
// each says what the card shows: a signatory change sets the company account's key to the proposed rule, a message part
// posts that part's text to the card's topic. Anything else is refused.
// - Phase 2a: the ScheduleCreate the server stored when it made it (plTransaction.serialisedTransaction), which catches
//   nodeStream's own bugs.
// - Phase 2b: the schedule as the public ledger's mirror node holds it, read by the schedule id the served ScheduleSign
//   names, which nodeStream can't change: a server that serves a ScheduleSign of another schedule than the one it stored is
//   caught here.
// Pure: the service gathers the rows and the mirror's record and calls it before it decrypts the key.
//
// Decoding is done with @hiero-ledger/proto (pinned with the SDK). protobufjs drops fields it doesn't know, so every layer
// must re-encode to exactly the bytes it came from: an unknown, duplicated or oddly-encoded field is refused, never ignored.
// Values are compared as bytes, strings and oneof names (body.data), never with instanceof, so two copies of the proto
// classes can't make a match fail or pass.

// Why a check refused, in the order public-ledger/report-sign-mismatch (ns-lambda-ts) accepts them: keep the two identical
export const REASON_CODES = [
  'MALFORMED',
  'NOT_SCHEDULE_SIGN',
  'NOT_CANONICAL',
  'SIGNED_ALREADY',
  'SCHEDULE_MISMATCH',
  'PAYER_MISMATCH',
  'FEE_TOO_HIGH',
  'DURATION_TOO_LONG',
  'MEMO_NOT_EMPTY',
  'EXTRA_FIELDS',
  'CREATE_MISMATCH',
  'CONTENT_TYPE_MISMATCH',
  'ACCOUNT_MISMATCH',
  'KEY_MISMATCH',
  'TOPIC_MISMATCH',
  'MESSAGE_MISMATCH',
  'UNCHECKED_TYPE',
  'MIRROR_MISMATCH',
] as const;

export type SignCheckReason = (typeof REASON_CODES)[number];

export type SignCheckResult = { ok: true } | { ok: false; reason: SignCheckReason };

// Why the mirror check held back without a mismatch, so nothing is reported: the mirror couldn't be read, or the schedule
// is no longer open on the public ledger (executed, deleted or expired), so there is nothing left to sign
export type MirrorHoldReason = 'MIRROR_UNAVAILABLE' | 'SCHEDULE_CLOSED';

export type MirrorCheckResult = SignCheckResult | { ok: false; reason: MirrorHoldReason };

// The most a served ScheduleSign may charge the operating account: the SDK's ScheduleSign default (5 hbar), which the
// plugin's getScheduledTransactionSerialisation builds with
export const MAX_SCHEDULE_SIGN_FEE_TINYBARS = 500_000_000;
// The longest a served ScheduleSign may stay valid (the SDK default is 120 s)
export const MAX_VALID_DURATION_SECONDS = 180;
// The most the scheduled transaction may charge the operating account when it runs: the SDK default the plugin leaves
// on every scheduled account change and message (2 hbar)
export const MAX_SCHEDULED_FEE_TINYBARS = 200_000_000;

// What the schedule must do, from the database
export type ScheduledContent =
  | { kind: 'ACCOUNT_CHANGE'; accountId: string; key: proto.IKey }
  | { kind: 'MESSAGE_SUBMIT'; topicId: string; text: string }
  | { kind: 'UNCHECKED' };

// The company's operating account, as the user policy projects it: it pays for every schedule and is its admin key
export interface OperatingAccount {
  accountId: string;
  // DER hex, as the SDK's PublicKey.toString() writes it
  publicKey: string;
}

export interface ScheduleSignExpectation {
  // The schedule the plTransaction row is for
  scheduleId: string;
  // The row's transactionId: the ScheduleCreate's own id with "?scheduled"
  scheduledTransactionId: string;
  // The row's serialisedTransaction, decoded from base64: the ScheduleCreate as the server sent it
  createBytes: Uint8Array | null;
  operating: OperatingAccount;
  content: ScheduledContent;
}

class Refusal extends Error {
  constructor(readonly reason: SignCheckReason | MirrorHoldReason) {
    super(reason);
  }
}

const refuse = (reason: SignCheckReason | MirrorHoldReason): never => {
  throw new Refusal(reason);
};

const sameBytes = (a: Uint8Array | null | undefined, b: Uint8Array | null | undefined) =>
  !!a && !!b && a.length === b.length && a.every((byte, i) => byte === b[i]);

// A Long, number or missing proto integer as a decimal string (proto3 reads a missing integer as 0)
const int = (value: unknown) => (value === null || value === undefined ? '0' : String(value));

// A decoded message's fields that were on the wire (protobufjs makes those own properties; defaults sit on the prototype)
const ownKeys = (message: object) => Object.keys(message);

// Refuses with EXTRA_FIELDS when a message has a field set outside the allowed ones. An empty repeated field writes nothing,
// so it is allowed wherever it appears.
function onlyFields(message: object, allowed: string[]) {
  for (const key of ownKeys(message)) {
    const value = (message as Record<string, unknown>)[key];
    if (allowed.includes(key)) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    refuse('EXTRA_FIELDS');
  }
}

// "shard.realm.num" of an AccountID, ScheduleID or TopicID; null for an alias or anything but those three numbers
function entityId(id: object | null | undefined, numField: string): string | null {
  if (!id) return null;
  if (ownKeys(id).some((key) => !['shardNum', 'realmNum', numField].includes(key))) return null;
  const fields = id as Record<string, unknown>;
  return `${int(fields.shardNum)}.${int(fields.realmNum)}.${int(fields[numField])}`;
}

// An id as nodeStream stores it, "0.0.123"; null when it isn't one
const normalisedId = (id: unknown) => {
  const match = typeof id === 'string' ? /^(\d+)\.(\d+)\.(\d+)$/.exec(id) : null;
  return match ? `${Number(match[1])}.${Number(match[2])}.${Number(match[3])}` : null;
};

const matchesId = (decoded: string | null, expected: unknown) => decoded !== null && decoded === normalisedId(expected);

// "0.0.123@1700000000.000000123", as the SDK's TransactionId.toString() writes it (without "?scheduled")
function transactionIdText(id: proto.ITransactionID | null | undefined): string | null {
  const account = entityId(id?.accountID ?? null, 'accountNum');
  const start = id?.transactionValidStart;
  if (!account || !start) return null;
  return `${account}@${int(start.seconds)}.${int(start.nanos).padStart(9, '0')}`;
}

// The bytes, decoded with a re-encode that must give them back
function decodeExactly<T extends I, I extends object>(
  type: { decode(bytes: Uint8Array): T; encode(message: I): { finish(): Uint8Array } },
  bytes: Uint8Array | null | undefined,
): T {
  if (!bytes || bytes.length === 0) return refuse('MALFORMED');
  let message: T;
  try {
    message = type.decode(bytes);
  } catch {
    return refuse('MALFORMED');
  }
  if (!sameBytes(type.encode(message).finish(), bytes)) refuse('NOT_CANONICAL');
  return message;
}

// Every node's transaction body in a TransactionList (one per node), each layer re-encoded exactly. Each list entry may
// carry only its signedTransactionBytes: the legacy bodyBytes/sigMap fields would let the SDK sign nothing, or other bytes.
function nodeTransactions(bytes: Uint8Array): { signed: proto.SignedTransaction; body: proto.TransactionBody }[] {
  const list = decodeExactly(proto.TransactionList, bytes);
  const entries = list.transactionList ?? [];
  if (entries.length === 0) refuse('MALFORMED');
  return entries.map((entry) => {
    if (ownKeys(entry).join() !== 'signedTransactionBytes') refuse('NOT_CANONICAL');
    const signed = decodeExactly(proto.SignedTransaction, entry.signedTransactionBytes);
    const body = decodeExactly(proto.TransactionBody, signed.bodyBytes);
    return { signed, body };
  });
}

// The served ScheduleSign: unsigned, paid by the operating account, for this schedule, with nothing else in it
function checkScheduleSignBodies(bytes: Uint8Array, expected: ScheduleSignExpectation) {
  for (const { signed, body } of nodeTransactions(bytes)) {
    if (ownKeys(signed).some((key) => key !== 'bodyBytes' && key !== 'sigMap')) refuse('EXTRA_FIELDS');
    if ((signed.sigMap?.sigPair ?? []).length > 0) refuse('SIGNED_ALREADY');
    if (body.data !== 'scheduleSign') refuse('NOT_SCHEDULE_SIGN');
    onlyFields(body, [
      'transactionID',
      'nodeAccountID',
      'transactionFee',
      'transactionValidDuration',
      'memo',
      'scheduleSign',
    ]);

    const scheduleSign = body.scheduleSign as proto.ScheduleSignTransactionBody;
    onlyFields(scheduleSign, ['scheduleID']);
    if (!matchesId(entityId(scheduleSign.scheduleID ?? null, 'scheduleNum'), expected.scheduleId)) {
      refuse('SCHEDULE_MISMATCH');
    }

    const transactionId = body.transactionID;
    if (!transactionId) return refuse('PAYER_MISMATCH');
    onlyFields(transactionId, ['transactionValidStart', 'accountID', 'scheduled']);
    if (transactionId.scheduled) refuse('PAYER_MISMATCH');
    if (!matchesId(entityId(transactionId.accountID ?? null, 'accountNum'), expected.operating.accountId)) {
      refuse('PAYER_MISMATCH');
    }

    if (Number(int(body.transactionFee)) > MAX_SCHEDULE_SIGN_FEE_TINYBARS) refuse('FEE_TOO_HIGH');
    if (Number(int(body.transactionValidDuration?.seconds)) > MAX_VALID_DURATION_SECONDS) refuse('DURATION_TOO_LONG');
    if ((body.memo ?? '') !== '') refuse('MEMO_NOT_EMPTY');
  }
}

// The stored ScheduleCreate: the row's own, paid for and administered by the operating account; its scheduled body
function scheduledBody(expected: ScheduleSignExpectation): proto.SchedulableTransactionBody {
  if (!expected.createBytes || expected.createBytes.length === 0) return refuse('CREATE_MISMATCH');
  const nodes = nodeTransactions(expected.createBytes);
  const creates = nodes.map(({ body }) => {
    if (body.data !== 'scheduleCreate') refuse('CREATE_MISMATCH');
    return body.scheduleCreate as proto.ScheduleCreateTransactionBody;
  });
  // One schedule, sent to several nodes: every node's copy must say the same
  const [create] = creates;
  const encoded = proto.ScheduleCreateTransactionBody.encode(create).finish();
  if (creates.some((other) => !sameBytes(proto.ScheduleCreateTransactionBody.encode(other).finish(), encoded))) {
    refuse('CREATE_MISMATCH');
  }
  const ownId = expected.scheduledTransactionId.replace(/\?scheduled$/, '');
  if (nodes.some(({ body }) => transactionIdText(body.transactionID) !== ownId)) refuse('CREATE_MISMATCH');

  onlyFields(create, [
    'scheduledTransactionBody',
    'adminKey',
    'payerAccountID',
    'expirationTime',
    'memo',
    'waitForExpiry',
  ]);
  if ((create.memo ?? '') !== '' || create.waitForExpiry) refuse('CREATE_MISMATCH');
  if (!matchesId(entityId(create.payerAccountID ?? null, 'accountNum'), expected.operating.accountId)) {
    refuse('CREATE_MISMATCH');
  }
  const adminKey = create.adminKey;
  const operatingKey = ed25519Raw(expected.operating.publicKey);
  if (!adminKey || ownKeys(adminKey).join() !== 'ed25519' || !sameBytes(adminKey.ed25519, operatingKey)) {
    refuse('CREATE_MISMATCH');
  }

  return schedulable(create.scheduledTransactionBody as proto.SchedulableTransactionBody | null, 'CREATE_MISMATCH');
}

// What a schedule runs, holding nothing but its fee, an empty memo and the one transaction
function schedulable(
  inner: proto.SchedulableTransactionBody | null | undefined,
  missing: SignCheckReason,
): proto.SchedulableTransactionBody {
  if (!inner || !inner.data) return refuse(missing);
  onlyFields(inner, ['transactionFee', 'memo', inner.data]);
  if ((inner.memo ?? '') !== '') refuse('MEMO_NOT_EMPTY');
  if (Number(int(inner.transactionFee)) > MAX_SCHEDULED_FEE_TINYBARS) refuse('FEE_TOO_HIGH');
  return inner;
}

// The scheduled transaction does what the card shows, and only that
function checkContent(inner: proto.SchedulableTransactionBody, content: ScheduledContent) {
  if (content.kind === 'ACCOUNT_CHANGE') {
    if (inner.data !== 'cryptoUpdateAccount') refuse('CONTENT_TYPE_MISMATCH');
    const update = inner.cryptoUpdateAccount as proto.CryptoUpdateTransactionBody;
    onlyFields(update, ['accountIDToUpdate', 'key']);
    if (!matchesId(entityId(update.accountIDToUpdate ?? null, 'accountNum'), content.accountId)) {
      refuse('ACCOUNT_MISMATCH');
    }
    if (!update.key || !sameBytes(proto.Key.encode(update.key).finish(), proto.Key.encode(content.key).finish())) {
      refuse('KEY_MISMATCH');
    }
    return;
  }
  if (content.kind === 'MESSAGE_SUBMIT') {
    if (inner.data !== 'consensusSubmitMessage') refuse('CONTENT_TYPE_MISMATCH');
    const submit = inner.consensusSubmitMessage as proto.ConsensusSubmitMessageTransactionBody;
    onlyFields(submit, ['topicID', 'message']);
    if (!matchesId(entityId(submit.topicID ?? null, 'topicNum'), content.topicId)) refuse('TOPIC_MISMATCH');
    if (!sameBytes(submit.message, new TextEncoder().encode(content.text))) refuse('MESSAGE_MISMATCH');
    return;
  }
  refuse('UNCHECKED_TYPE');
}

/**
 * Whether the served ScheduleSign bytes may be signed: a bare ScheduleSign of the expected schedule, paid by the operating
 * account, whose schedule (read from the stored ScheduleCreate) does exactly what the card shows.
 */
export function checkScheduleSign(servedBytes: Uint8Array, expected: ScheduleSignExpectation): SignCheckResult {
  try {
    checkScheduleSignBodies(servedBytes, expected);
    // A type with no content check is refused before its stored bytes are read: they may not be a ScheduleCreate at all
    if (expected.content.kind === 'UNCHECKED') refuse('UNCHECKED_TYPE');
    checkContent(scheduledBody(expected), expected.content);
    return { ok: true };
  } catch (err) {
    if (err instanceof Refusal) return { ok: false, reason: err.reason as SignCheckReason };
    return { ok: false, reason: 'MALFORMED' };
  }
}

// A plain object's own field, or undefined
const field = (value: unknown, key: string): unknown =>
  typeof value === 'object' && value !== null && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Record<string, unknown>)[key]
    : undefined;

// The whole seconds of a mirror consensus timestamp ('seconds.nanoseconds'), or null
const timestampSeconds = (value: unknown) => {
  const match = typeof value === 'string' ? /^(\d+)(?:\.\d+)?$/.exec(value) : null;
  return match ? Number(match[1]) : null;
};

const hex = (bytes: Uint8Array) => Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

export interface MirrorScheduleExpectation {
  // The schedule the served ScheduleSign names, which the stored-bytes check has matched with the row's
  scheduleId: string;
  operating: OperatingAccount;
  content: ScheduledContent;
}

/**
 * Whether the schedule, as the mirror node holds it (GET /api/v1/schedules/{id}), is the open schedule the card shows:
 * this schedule id, made and paid for by the operating account with its key as the only admin key, no memo, not waiting
 * for its expiry, and running exactly what the card shows. One already executed, deleted or expired is SCHEDULE_CLOSED,
 * not a mismatch. `nowSeconds` is the current time in seconds.
 */
export function checkMirrorSchedule(
  record: unknown,
  expected: MirrorScheduleExpectation,
  nowSeconds: number,
): MirrorCheckResult {
  try {
    if (typeof record !== 'object' || record === null) refuse('MIRROR_MISMATCH');
    if (!matchesId(normalisedId(field(record, 'schedule_id')), expected.scheduleId)) refuse('MIRROR_MISMATCH');

    const expiresAt = field(record, 'expiration_time');
    if (field(record, 'deleted') === true) refuse('SCHEDULE_CLOSED');
    if (field(record, 'executed_timestamp') !== null && field(record, 'executed_timestamp') !== undefined) {
      refuse('SCHEDULE_CLOSED');
    }
    const expirySeconds = timestampSeconds(expiresAt);
    if (expiresAt !== null && expiresAt !== undefined && expirySeconds === null) refuse('MIRROR_MISMATCH');
    if (expirySeconds !== null && expirySeconds <= nowSeconds) refuse('SCHEDULE_CLOSED');
    if (field(record, 'deleted') !== false) refuse('MIRROR_MISMATCH');

    if (!matchesId(normalisedId(field(record, 'creator_account_id')), expected.operating.accountId)) {
      refuse('MIRROR_MISMATCH');
    }
    if (!matchesId(normalisedId(field(record, 'payer_account_id')), expected.operating.accountId)) {
      refuse('MIRROR_MISMATCH');
    }
    const adminKey = field(record, 'admin_key');
    const operatingKey = ed25519Raw(expected.operating.publicKey);
    const adminHex = field(adminKey, 'key');
    if (
      !operatingKey ||
      field(adminKey, '_type') !== 'ED25519' ||
      typeof adminHex !== 'string' ||
      adminHex.toLowerCase() !== hex(operatingKey)
    ) {
      refuse('MIRROR_MISMATCH');
    }
    if (field(record, 'memo') !== '' || field(record, 'wait_for_expiry') !== false) refuse('MIRROR_MISMATCH');

    if (expected.content.kind === 'UNCHECKED') refuse('UNCHECKED_TYPE');
    const bodyBytes = base64Bytes(field(record, 'transaction_body') as string | null | undefined);
    let inner: proto.SchedulableTransactionBody;
    try {
      inner = decodeExactly(proto.SchedulableTransactionBody, bodyBytes);
    } catch {
      // The mirror's own encoding: what it couldn't account for is the mirror's record, not what nodeStream served
      return { ok: false, reason: 'MIRROR_MISMATCH' };
    }
    checkContent(schedulable(inner, 'MIRROR_MISMATCH'), expected.content);
    return { ok: true };
  } catch (err) {
    if (err instanceof Refusal) return { ok: false, reason: err.reason };
    return { ok: false, reason: 'MIRROR_MISMATCH' };
  }
}

// What the browser has read to build a part's expectation
export interface SignCheckContext {
  // The company's own ledger account (company.ledgerCred.accountId), which a signatory change updates
  companyAccountId: string | null | undefined;
  // The message the part belongs to (plTransaction.consensusService.messageId), as the user policy projects it
  message: Pick<PublicLedgerMessage, 'type' | 'content'> | null | undefined;
  // The ledger id of the part's topic (plTopic.topicId of plTransaction.consensusService.topicId)
  ledgerTopicId: string | null | undefined;
  // A colleague's public key (people.ledgerCred.publicKey)
  publicKeyOf: (personId: string) => string | null | undefined;
}

/**
 * What a transaction's schedule must do, from its row and what the browser read about it, or why it can't be known.
 * A signatory change sets the company account's key to the proposed rule; a message part posts that part's text (for a
 * HASHED message, the posted hash the server stored as its text). Other types, and ENCRYPTED messages, have no check yet.
 */
export function scheduledContentFor(
  row: Pick<PublicLedgerTransaction, 'type' | 'proposedKey' | 'partIndex'>,
  context: SignCheckContext,
): ScheduledContent | SignCheckReason {
  if (row.type === 'UPDATE_ACCOUNT_TRANSACTION') {
    if (!normalisedId(context.companyAccountId)) return 'ACCOUNT_MISMATCH';
    try {
      const key = ledgerKeyFromRule(row.proposedKey, context.publicKeyOf);
      return { kind: 'ACCOUNT_CHANGE', accountId: context.companyAccountId as string, key };
    } catch {
      return 'KEY_MISMATCH';
    }
  }
  if (row.type === 'SUBMIT_TOPIC_MESSAGE_TRANSACTION') {
    const message = context.message;
    if (!message || (message.type !== 'PLAIN_TEXT' && message.type !== 'HASHED')) return 'UNCHECKED_TYPE';
    if (!normalisedId(context.ledgerTopicId)) return 'TOPIC_MISMATCH';
    const text =
      typeof message.content?.text === 'string' ? messagePartText(message.content.text, row.partIndex) : null;
    if (text === null) return 'MESSAGE_MISMATCH';
    return { kind: 'MESSAGE_SUBMIT', topicId: context.ledgerTopicId as string, text };
  }
  return { kind: 'UNCHECKED' };
}

// Base64 as bytes (atob is in browsers and Node); null when it isn't base64
function base64Bytes(text: string | null | undefined): Uint8Array | null {
  if (typeof text !== 'string' || text === '') return null;
  try {
    return Uint8Array.from(atob(text), (char) => char.charCodeAt(0));
  } catch {
    return null;
  }
}

/**
 * The check of one served part (its base64 ScheduleSign, and the scheduleId the server sent with it) against its row: the
 * row's schedule, its stored ScheduleCreate and what it must do.
 */
export function checkSigningPart(
  served: { bytes: string; scheduleId?: string | null },
  row: Pick<
    PublicLedgerTransaction,
    'type' | 'scheduleId' | 'transactionId' | 'proposedKey' | 'partIndex' | 'serialisedTransaction'
  >,
  operating: OperatingAccount | null | undefined,
  context: SignCheckContext,
): SignCheckResult {
  const servedBytes = base64Bytes(served.bytes);
  if (!servedBytes) return { ok: false, reason: 'MALFORMED' };
  if (!operating || !normalisedId(operating.accountId) || !ed25519Raw(operating.publicKey)) {
    return { ok: false, reason: 'PAYER_MISMATCH' };
  }
  if (!row.scheduleId || (typeof served.scheduleId === 'string' && served.scheduleId !== row.scheduleId)) {
    return { ok: false, reason: 'SCHEDULE_MISMATCH' };
  }
  const content = scheduledContentFor(row, context);
  const result = checkScheduleSign(servedBytes, {
    scheduleId: row.scheduleId,
    scheduledTransactionId: row.transactionId ?? '',
    createBytes: base64Bytes(row.serialisedTransaction),
    operating,
    content: typeof content === 'string' ? { kind: 'UNCHECKED' } : content,
  });
  // The served bytes are checked first, so a bad ScheduleSign is reported as such even when the content can't be known
  if (typeof content === 'string' && !result.ok && result.reason === 'UNCHECKED_TYPE') {
    return { ok: false, reason: content };
  }
  return result;
}

// What the signing card showed when it rendered, kept from its first read: the check must assert the transaction says
// this, not what a later read of the same rows says, or a server could show one thing and serve another at the press
export interface ShownOnCard {
  // The kind of change the card rendered (from its job), null for a kind it has no view of
  kind: 'ACCOUNT_CHANGE' | 'MESSAGE_SUBMIT' | null;
  // The message the card showed (null for a signatory change): its id, type and content as read when the card loaded
  message: { id: string; type: string; text: string | null; collection: string | null; entityId: string | null } | null;
  // The proposed signatory rule the card summarised (plTransaction.proposedKey), null for a message
  proposedKey: unknown;
}

// Plain data with its object keys sorted, so two reads of the same JSON compare equal whatever order they came in
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, canonical((value as Record<string, unknown>)[key])]),
    );
  }
  return value;
}

const sameData = (a: unknown, b: unknown) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));

// Whether every key in a stored rule is one the card's Proposed line names (a person, or a group of them): it leaves out
// a raw ED25519 key or an unknown type (ruleFromRequirement), which would sign in a signer nobody was shown
function onlyShownKeys(key: unknown): boolean {
  if (typeof key !== 'object' || key === null) return false;
  const { keyType, signatoryId, keys } = key as { keyType?: unknown; signatoryId?: unknown; keys?: unknown };
  if (keyType === 'PERSON_ID') return typeof signatoryId === 'string' && signatoryId !== '';
  if (keyType !== 'THRESHOLD_KEY' && keyType !== 'KEY_LIST') return false;
  return Array.isArray(keys) && keys.length > 0 && keys.every(onlyShownKeys);
}

/**
 * Whether what the check read for a part is what the card showed: a signatory change's proposed rule, or the message the
 * part belongs to (its id, type and content). The reason it isn't, or null.
 */
export function shownMismatch(
  row: Pick<PublicLedgerTransaction, 'type' | 'proposedKey' | 'consensusService'>,
  message: Pick<PublicLedgerMessage, 'type' | 'content'> | null | undefined,
  shown: ShownOnCard,
): SignCheckReason | null {
  if (row.type === 'UPDATE_ACCOUNT_TRANSACTION') {
    if (shown.kind !== 'ACCOUNT_CHANGE') return 'CONTENT_TYPE_MISMATCH';
    if (shown.proposedKey === null || shown.proposedKey === undefined) return 'KEY_MISMATCH';
    if (!sameData(row.proposedKey, shown.proposedKey) || !onlyShownKeys(shown.proposedKey)) return 'KEY_MISMATCH';
    return null;
  }
  if (row.type === 'SUBMIT_TOPIC_MESSAGE_TRANSACTION') {
    if (shown.kind !== 'MESSAGE_SUBMIT') return 'CONTENT_TYPE_MISMATCH';
    const card = shown.message;
    if (!card || !message || row.consensusService?.messageId !== card.id || message.type !== card.type) {
      return 'MESSAGE_MISMATCH';
    }
    const content = (message.content ?? {}) as { text?: unknown; collection?: unknown; entityId?: unknown };
    const same = (value: unknown, kept: string | null) => (typeof value === 'string' ? value : null) === kept;
    if (!same(content.text, card.text) || !same(content.collection, card.collection)) return 'MESSAGE_MISMATCH';
    if (!same(content.entityId, card.entityId)) return 'MESSAGE_MISMATCH';
    return null;
  }
  // Any other type has no view on the card, and the checks after this refuse it
  return shown.kind === null ? null : 'CONTENT_TYPE_MISMATCH';
}

/**
 * The mirror check of one part that has passed checkSigningPart: the mirror's record of the row's schedule (null when the
 * mirror couldn't be read) against what the row must do.
 */
export function checkSigningPartOnMirror(
  record: unknown,
  row: Pick<PublicLedgerTransaction, 'type' | 'scheduleId' | 'proposedKey' | 'partIndex'>,
  operating: OperatingAccount,
  context: SignCheckContext,
  nowSeconds: number,
): MirrorCheckResult {
  if (record === null || record === undefined) return { ok: false, reason: 'MIRROR_UNAVAILABLE' };
  const content = scheduledContentFor(row, context);
  if (typeof content === 'string') return { ok: false, reason: content };
  return checkMirrorSchedule(record, { scheduleId: row.scheduleId ?? '', operating, content }, nowSeconds);
}

// --- The automatic payments approval (#25 DECISION 4.7, "Open (depend on #24)") ---
//
// The approval is signed through a ScheduleSign the server builds and nodeStream's platform account pays: the browser is
// handed its body bytes and signs those alone, so a server could hand every signatory the same body of anything (a key
// change of the company account, say) and collect raw signatures under any signing rule. Before it signs, the browser
// decodes the body with the rest of this module's rules (every layer re-encodes to exactly its bytes, nothing unknown),
// requires a ScheduleSign, binds the schedule id from the decoded body, and reads that schedule from the public mirror:
// one hbar allowance of the ledger's maximum from the company account to its operating account, created and paid for by
// the accounts the row names, the memo the browser recomputes from the settings it showed (helpers/auto-payments.ts
// approveMemo), and nothing else. The ScheduleSign is always paid by nodeStream's platform account (never the company's,
// the operating or the signer's own). The schedule is created and paid by the platform account for a set-up or re-sign,
// and by the company's operating account for a change of amounts (#25 phase 6, DECISION 4.1 a, G2 a); it is never the
// company's account or the signer's own.

// The ledger's maximum allowance (Long.MAX, #25 DECISION 1.3), compared as a string
const MAX_ALLOWANCE_TINYBARS = '9223372036854775807';

export interface ApproveExpectation {
  // The schedule the row names
  scheduleId: string;
  // The allowance's owner: the company's account (company.ledgerCred.accountId)
  companyAccountId: string;
  // The allowance's spender: the operating account, from #24's user read of plOperatingAccount (DECISION 2.5)
  operatingAccountId: string;
  // The account that pays the ScheduleSign served to sign: nodeStream's platform account, as the row names it
  // (plTransaction.allowance.signPayerAccountId, or payerAccountId for a set-up or re-sign)
  signPayerAccountId: string;
  // The account that created the schedule, and the one that pays the approval when it runs, as the mirror must show them:
  // the platform account for a set-up or re-sign; the operating account for a change of amounts
  scheduleCreatorAccountId: string;
  schedulePayerAccountId: string;
  // Accounts that may never pay the ScheduleSign: the signer's own, the company's and the operating account. They may never
  // create or pay the schedule either, except the operating account where the expectation names it (a change of amounts)
  notPayers: (string | null | undefined)[];
  // The approval's memo, recomputed from the settings the card showed
  memo: string;
}

export interface ApproveScheduleAccounts {
  // Whether the operating account made the schedule (a change of amounts); the caller checks it is the company's own
  operating: boolean;
  creatorAccountId: string;
  payerAccountId: string;
  signPayerAccountId: string;
}

/**
 * Who the approval's row says made and pays its schedule, and who pays each signature, or null when the row doesn't say
 * it plainly (nothing is then signed). A set-up or re-sign (payer PLATFORM) is the platform account's throughout: an older
 * row naming only payerAccountId stands for all three. A change of amounts (payer OPERATING, #25 phase 6) has the schedule
 * made and paid by one account, the operating one, and every signature paid by another, the platform.
 */
export function approveScheduleAccounts(
  allowance: PublicLedgerTransaction['allowance'] | null | undefined,
): ApproveScheduleAccounts | null {
  const payer = allowance?.payerAccountId ?? null;
  if (!payer) return null;
  const creator = allowance?.creatorAccountId ?? null;
  const signPayer = allowance?.signPayerAccountId ?? null;
  const same = (a: string, b: string) => normalisedId(a) !== null && normalisedId(a) === normalisedId(b);
  if (allowance?.payer === 'OPERATING') {
    if (!creator || !signPayer || !same(creator, payer) || same(signPayer, creator)) return null;
    return { operating: true, creatorAccountId: creator, payerAccountId: payer, signPayerAccountId: signPayer };
  }
  if ((allowance?.payer ?? 'PLATFORM') !== 'PLATFORM') return null;
  if ((creator && !same(creator, payer)) || (signPayer && !same(signPayer, payer))) return null;
  return { operating: false, creatorAccountId: payer, payerAccountId: payer, signPayerAccountId: payer };
}

// The account must be the named one, and none of the accounts that may never pay, except the operating account when it is
// the one named (a change of amounts' schedule, which the operating account creates and pays)
function checkPayer(decoded: string | null, named: string, expected: ApproveExpectation, reason: SignCheckReason) {
  if (!matchesId(decoded, named)) refuse(reason);
  const operating = normalisedId(expected.operatingAccountId);
  const operatingNamed = operating !== null && normalisedId(named) === operating;
  const barred = expected.notPayers.filter((id) => !(operatingNamed && normalisedId(id) === operating));
  if (barred.some((id) => matchesId(decoded, id))) refuse(reason);
}

/**
 * Whether the body bytes the server served for an approval may be signed: exactly one TransactionBody (it re-encodes to
 * the same bytes), a ScheduleSign of the expected schedule and nothing else, paid by the expected payer and none of the
 * company's or the signer's accounts, with no memo, a capped fee and a short validity.
 */
export function checkApproveScheduleSign(bodyBytes: Uint8Array, expected: ApproveExpectation): SignCheckResult {
  try {
    const body = decodeExactly(proto.TransactionBody, bodyBytes);
    if (body.data !== 'scheduleSign') refuse('NOT_SCHEDULE_SIGN');
    onlyFields(body, [
      'transactionID',
      'nodeAccountID',
      'transactionFee',
      'transactionValidDuration',
      'memo',
      'scheduleSign',
    ]);
    const scheduleSign = body.scheduleSign as proto.ScheduleSignTransactionBody;
    onlyFields(scheduleSign, ['scheduleID']);
    if (!matchesId(entityId(scheduleSign.scheduleID ?? null, 'scheduleNum'), expected.scheduleId)) {
      refuse('SCHEDULE_MISMATCH');
    }
    const transactionId = body.transactionID;
    if (!transactionId) return refuse('PAYER_MISMATCH');
    onlyFields(transactionId, ['transactionValidStart', 'accountID', 'scheduled']);
    if (transactionId.scheduled) refuse('PAYER_MISMATCH');
    // The ScheduleSign is nodeStream's to pay, never the operating account's, even for a change of amounts
    const signPayer = entityId(transactionId.accountID ?? null, 'accountNum');
    if (matchesId(normalisedId(expected.signPayerAccountId), expected.operatingAccountId)) refuse('PAYER_MISMATCH');
    checkPayer(signPayer, expected.signPayerAccountId, expected, 'PAYER_MISMATCH');
    if (Number(int(body.transactionFee)) > MAX_SCHEDULE_SIGN_FEE_TINYBARS) refuse('FEE_TOO_HIGH');
    if (Number(int(body.transactionValidDuration?.seconds)) > MAX_VALID_DURATION_SECONDS) refuse('DURATION_TOO_LONG');
    if ((body.memo ?? '') !== '') refuse('MEMO_NOT_EMPTY');
    return { ok: true };
  } catch (err) {
    if (err instanceof Refusal) return { ok: false, reason: err.reason as SignCheckReason };
    return { ok: false, reason: 'MALFORMED' };
  }
}

/**
 * The checks every server-built automatic payments schedule shares on the mirror (an approval, or round 15's card change
 * record): this schedule id, open (one executed, deleted or expired is SCHEDULE_CLOSED), created by the expected creator and
 * paid for by the expected payer, the expected memo, not waiting for its expiry. Answers what it runs, decoded; throws a
 * Refusal otherwise.
 */
function checkServerSchedule(
  record: unknown,
  expected: ApproveExpectation,
  nowSeconds: number,
): proto.SchedulableTransactionBody {
  if (typeof record !== 'object' || record === null) return refuse('MIRROR_MISMATCH');
  if (!matchesId(normalisedId(field(record, 'schedule_id')), expected.scheduleId)) refuse('MIRROR_MISMATCH');

  const expiresAt = field(record, 'expiration_time');
  if (field(record, 'deleted') === true) refuse('SCHEDULE_CLOSED');
  if (field(record, 'executed_timestamp') !== null && field(record, 'executed_timestamp') !== undefined) {
    refuse('SCHEDULE_CLOSED');
  }
  const expirySeconds = timestampSeconds(expiresAt);
  if (expiresAt !== null && expiresAt !== undefined && expirySeconds === null) refuse('MIRROR_MISMATCH');
  if (expirySeconds !== null && expirySeconds <= nowSeconds) refuse('SCHEDULE_CLOSED');
  if (field(record, 'deleted') !== false) refuse('MIRROR_MISMATCH');

  const creator = normalisedId(field(record, 'creator_account_id'));
  checkPayer(creator, expected.scheduleCreatorAccountId, expected, 'MIRROR_MISMATCH');
  checkPayer(
    normalisedId(field(record, 'payer_account_id')),
    expected.schedulePayerAccountId,
    expected,
    'MIRROR_MISMATCH',
  );
  if (field(record, 'memo') !== expected.memo || field(record, 'wait_for_expiry') !== false) refuse('MIRROR_MISMATCH');

  const bodyBytes = base64Bytes(field(record, 'transaction_body') as string | null | undefined);
  try {
    return decodeExactly(proto.SchedulableTransactionBody, bodyBytes);
  } catch {
    // The mirror's own encoding: what it couldn't account for is the mirror's record, not what nodeStream served
    return refuse('MIRROR_MISMATCH');
  }
}

/**
 * Whether the approval's schedule, as the mirror node holds it, is the open approval the card shows: this schedule id,
 * created by the expected creator and paid for by the expected payer, the recomputed memo on the schedule and on what it
 * runs, not waiting for its expiry, and running exactly one hbar allowance of the ledger's maximum from the company account
 * to the operating account, with no token or NFT allowance and nothing else. One already executed, deleted or expired is
 * SCHEDULE_CLOSED. The admin key isn't checked: it is the creator's, which may only delete the schedule, never make it run
 * otherwise.
 */
export function checkApproveOnMirror(
  record: unknown,
  expected: ApproveExpectation,
  nowSeconds: number,
): MirrorCheckResult {
  return checkAllowanceOnMirror(record, expected, nowSeconds, MAX_ALLOWANCE_TINYBARS);
}

/**
 * #40: whether a stop of automatic payments, as the mirror node holds it, is the open stop the card shows: checked as an
 * approval is (checkApproveOnMirror), with an allowance of exactly 0 from the company account to the operating account,
 * which removes the allowance. Never the maximum.
 */
export function checkStopOnMirror(
  record: unknown,
  expected: ApproveExpectation,
  nowSeconds: number,
): MirrorCheckResult {
  return checkAllowanceOnMirror(record, expected, nowSeconds, '0');
}

function checkAllowanceOnMirror(
  record: unknown,
  expected: ApproveExpectation,
  nowSeconds: number,
  tinybars: string,
): MirrorCheckResult {
  if (record === null || record === undefined) return { ok: false, reason: 'MIRROR_UNAVAILABLE' };
  try {
    const inner = checkServerSchedule(record, expected, nowSeconds);
    if (inner.data !== 'cryptoApproveAllowance') refuse('CONTENT_TYPE_MISMATCH');
    onlyFields(inner, ['transactionFee', 'memo', 'cryptoApproveAllowance']);
    if ((inner.memo ?? '') !== expected.memo) refuse('MIRROR_MISMATCH');
    const approve = inner.cryptoApproveAllowance as proto.CryptoApproveAllowanceTransactionBody;
    onlyFields(approve, ['cryptoAllowances']);
    const allowances = approve.cryptoAllowances ?? [];
    if (allowances.length !== 1) refuse('MIRROR_MISMATCH');
    const [allowance] = allowances;
    onlyFields(allowance, ['owner', 'spender', 'amount']);
    if (!matchesId(entityId(allowance.owner ?? null, 'accountNum'), expected.companyAccountId)) {
      refuse('ACCOUNT_MISMATCH');
    }
    if (!matchesId(entityId(allowance.spender ?? null, 'accountNum'), expected.operatingAccountId)) {
      refuse('ACCOUNT_MISMATCH');
    }
    // An amount of 0 isn't written on the wire: int reads it as '0'
    if (int(allowance.amount) !== tinybars) refuse('MIRROR_MISMATCH');
    return { ok: true };
  } catch (err) {
    if (err instanceof Refusal) return { ok: false, reason: err.reason };
    return { ok: false, reason: 'MIRROR_MISMATCH' };
  }
}

/**
 * #25 phase 6 (round 15): whether a card change's record, as the mirror node holds it, is the open record the card shows:
 * the same schedule checks as an approval's (created and paid for by the operating account, the memo the card shows on the
 * schedule and on what it runs, not waiting for its expiry), running an hbar transfer of exactly 1 tinybar from the
 * company account to the operating account (in either order), with no token transfer, no approved transfer and nothing
 * else. One already executed, deleted or expired is SCHEDULE_CLOSED.
 */
export function checkCardChangeOnMirror(
  record: unknown,
  expected: ApproveExpectation,
  nowSeconds: number,
): MirrorCheckResult {
  if (record === null || record === undefined) return { ok: false, reason: 'MIRROR_UNAVAILABLE' };
  try {
    const inner = checkServerSchedule(record, expected, nowSeconds);
    if (inner.data !== 'cryptoTransfer') refuse('CONTENT_TYPE_MISMATCH');
    onlyFields(inner, ['transactionFee', 'memo', 'cryptoTransfer']);
    if ((inner.memo ?? '') !== expected.memo) refuse('MIRROR_MISMATCH');
    const transfer = inner.cryptoTransfer as proto.CryptoTransferTransactionBody;
    onlyFields(transfer, ['transfers']);
    const list = transfer.transfers as proto.TransferList | null | undefined;
    if (!list) return refuse('MIRROR_MISMATCH');
    onlyFields(list, ['accountAmounts']);
    const amounts = list.accountAmounts ?? [];
    if (amounts.length !== 2) refuse('MIRROR_MISMATCH');
    const company = normalisedId(expected.companyAccountId);
    const operating = normalisedId(expected.operatingAccountId);
    if (!company || !operating || company === operating) return refuse('ACCOUNT_MISMATCH');
    const moved = new Map<string, string>();
    for (const amount of amounts) {
      // Nothing else on an entry, and no approved transfer (the SDK writes isApproval false on every entry)
      onlyFields(amount, ['accountID', 'amount', 'isApproval']);
      if (amount.isApproval) refuse('MIRROR_MISMATCH');
      const accountId = entityId(amount.accountID ?? null, 'accountNum');
      if (!accountId || moved.has(accountId)) return refuse('ACCOUNT_MISMATCH');
      moved.set(accountId, int(amount.amount));
    }
    if (!moved.has(company) || !moved.has(operating)) refuse('ACCOUNT_MISMATCH');
    if (moved.get(company) !== '-1' || moved.get(operating) !== '1') refuse('MIRROR_MISMATCH');
    return { ok: true };
  } catch (err) {
    if (err instanceof Refusal) return { ok: false, reason: err.reason };
    return { ok: false, reason: 'MIRROR_MISMATCH' };
  }
}
